/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Pure model/helpers for the Trade Task Template Builder (no React/MUI).
 */

import { Task, TemplateVersion, Trade, Tenant, GlobalDefaultTemplate, Scope, PhaseId, ForkPointer } from './types';

// ── WBS (task_uid) helpers ───────────────────────────────────────────
// The WBS number IS the hierarchy: "1.2.3"'s parent is "1.2".
export function deriveParentUid(uid: string): string {
  const parts = uid.split('.');
  return parts.length > 1 ? parts.slice(0, -1).join('.') : '';
}

export function nextTopLevelUid(tasks: Task[]): string {
  const tops = tasks.map(t => t.task_uid).filter(u => !u.includes('.')).map(Number).filter(n => !isNaN(n));
  return String((tops.length ? Math.max(...tops) : 0) + 1);
}

export function nextChildUid(tasks: Task[], parentUid: string): string {
  const prefix = parentUid + '.';
  const childNums = tasks.map(t => t.task_uid)
    .filter(u => u.startsWith(prefix) && deriveParentUid(u) === parentUid)
    .map(u => Number(u.slice(prefix.length))).filter(n => !isNaN(n));
  return `${parentUid}.${(childNums.length ? Math.max(...childNums) : 0) + 1}`;
}

export function nextOrderFor(tasks: Task[], parentUid: string): number {
  const parent = tasks.find(t => t.task_uid === parentUid);
  const siblingOrders = tasks.filter(t => deriveParentUid(t.task_uid) === parentUid).map(t => t.task_order);
  const base = siblingOrders.length ? Math.max(...siblingOrders) : (parent ? parent.task_order : Math.max(0, ...tasks.map(t => t.task_order)));
  return base + 100;
}

/**
 * Cascade a WBS rename/move onto every descendant of the renamed task, AND re-slot the moved
 * subtree's task_order to sit under its (possibly new) parent. `tasks` must already have the
 * renamed task itself patched to `newUid` (task_uid + parent_task_uid) before calling this.
 *
 * The WBS number IS the hierarchy — deriveParentUid/isMilestoneUid/computeEstimate/directChildren
 * all key off a task's OWN task_uid, never parent_task_uid. So renaming "1.5" to "1.6" must rewrite
 * every descendant's task_uid prefix too ("1.5.1" -> "1.6.1"), not just their parent_task_uid field:
 * leaving the old prefix in place makes deriveParentUid(child) point at a uid that no longer
 * exists — the child becomes an invisible orphan in the outline/canvas, AND the renamed parent's
 * own isMilestoneUid flips to false (it looks childless), silently reverting its Est. days/hours
 * from a derived sum back to a stale, directly-editable value that serializeTasksForStorage then
 * persists verbatim on save.
 */
export function cascadeRename(tasks: Task[], renameFrom: string, newUid: string): Task[] {
  const oldPrefix = renameFrom + '.';
  const rewritten = tasks.map(t => {
    if (t.task_uid === newUid || !t.task_uid.startsWith(oldPrefix)) return t;
    const uid = newUid + t.task_uid.slice(renameFrom.length);
    return { ...t, task_uid: uid, parent_task_uid: deriveParentUid(uid) };
  });

  // The WBS number can be rewritten to sit under a DIFFERENT parent (e.g. a task under 1.5
  // rewritten to "1.7.1"). parent_task_uid alone isn't what positions it in the phase-sorted
  // outline/canvas — task_order is — so without this it stays visually wedged among its old
  // parent's siblings even though its parent is now correct. Re-slot it (and its descendants,
  // now keyed by the NEW uid prefix) to the end of the new parent's children.
  const moved = rewritten.find(t => t.task_uid === newUid);
  if (!moved) return rewritten;
  const newParentUid = deriveParentUid(newUid);
  const newOrder = nextOrderFor(rewritten.filter(t => t.task_uid !== newUid), newParentUid);
  const delta = newOrder - moved.task_order;
  if (delta === 0) return rewritten;
  const newPrefix = newUid + '.';
  return rewritten.map(t => (t.task_uid === newUid || t.task_uid.startsWith(newPrefix))
    ? { ...t, task_order: t.task_order + delta } : t);
}

/**
 * Insert a brand-new sibling immediately after `afterUid`, at the same WBS depth/parent — never a
 * child of it (that's addChild's job; this is for a "main task" the user wants slotted between two
 * existing ones, not nested under one). Every sibling numbered after the insertion point, and all of
 * its descendants, gets its WBS uid relabeled up by one segment so the new task reads naturally in
 * sequence: inserting after "1.4" when "1.5" already exists relabels "1.5" -> "1.6", "1.5.1" ->
 * "1.6.1", etc. task_order is left untouched on every relabeled task — only the new task gets a
 * fresh order value, slotted between its neighbors — so no dependency reference (keyed by
 * task_order, never task_uid) is ever disturbed by the relabeling.
 *
 * Returns the renumbered tasks (the new task itself isn't added here — the caller builds it with the
 * returned newUid/newOrder, same division of labor as addTask/addChild in TradeTemplateSystem.tsx).
 * Callers must not invoke this with a phase root (depth 0) — that would mint a disconnected 5th
 * "phase" entry; TradeTemplateSystem.tsx's insertTaskAfter guards against that before calling in.
 */
export function insertSiblingAfter(tasks: Task[], afterUid: string): { tasks: Task[]; newUid: string; newOrder: number } {
  const after = tasks.find(t => t.task_uid === afterUid);
  if (!after) return { tasks, newUid: afterUid, newOrder: 0 };

  const parentUid = deriveParentUid(afterUid);
  const prefix = parentUid ? `${parentUid}.` : '';
  const segmentOf = (uid: string) => Number(uid.slice(prefix.length));
  const newSeg = segmentOf(afterUid) + 1;
  const newUid = `${prefix}${newSeg}`;

  // Siblings numbered >= newSeg must shift up by one segment to make room — highest first, so a
  // shift never collides with a uid another shift hasn't vacated yet.
  const toShift = directChildren(tasks, parentUid)
    .filter(s => segmentOf(s.task_uid) >= newSeg)
    .sort((a, b) => segmentOf(b.task_uid) - segmentOf(a.task_uid));

  let out = tasks;
  for (const sib of toShift) {
    const oldUid = sib.task_uid;
    const shiftedUid = `${prefix}${segmentOf(oldUid) + 1}`;
    const oldDescendantPrefix = `${oldUid}.`;
    out = out.map(t => {
      if (t.task_uid === oldUid) return { ...t, task_uid: shiftedUid };
      if (t.task_uid.startsWith(oldDescendantPrefix)) {
        const uid = shiftedUid + t.task_uid.slice(oldUid.length);
        return { ...t, task_uid: uid, parent_task_uid: deriveParentUid(uid) };
      }
      return t;
    });
  }

  // Slot the new task's order between "after" and whichever sibling now immediately follows it — the
  // outline/canvas sort by task_order, not by uid — or past everything if "after" was the last one.
  const following = directChildren(out, parentUid)
    .filter(t => t.task_order > after.task_order)
    .sort((a, b) => a.task_order - b.task_order)[0];
  const newOrder = following ? (after.task_order + following.task_order) / 2 : nextOrderFor(out, parentUid);

  return { tasks: out, newUid, newOrder };
}

export function phaseRootUid(tasks: Task[], phase: PhaseId): string | null {
  return tasks.find(t => t.phase === phase && !t.task_uid.includes('.'))?.task_uid ?? null;
}

// A task is a milestone iff it has sub-tasks. NOT user-editable — always derive this,
// never trust/display an is_milestone flag as a toggle.
export function isMilestoneUid(tasks: Task[], uid: string): boolean {
  return tasks.some(t => deriveParentUid(t.task_uid) === uid);
}

export function taskDepth(uid: string): number { return uid.split('.').length - 1; }

/**
 * Remove `uid` and all its descendants from `tasks`, AND strip any now-dangling dependency
 * reference (both `dependencies` and `dependency_classes`, keyed by task_order) that pointed at
 * one of the removed tasks. Without this, a task that depended on the deleted one keeps showing
 * a broken "#<order>" chip forever (see TaskCanvas's dependency chip fallback label) since nothing
 * else ever clears a dependency once its target is gone.
 */
export function removeTaskAndDescendants(tasks: Task[], uid: string): Task[] {
  const prefix = uid + '.';
  const removedOrders = new Set(
    tasks.filter(t => t.task_uid === uid || t.task_uid.startsWith(prefix)).map(t => t.task_order),
  );
  return tasks
    .filter(t => t.task_uid !== uid && !t.task_uid.startsWith(prefix))
    .map(t => {
      if (!t.dependencies.some(d => removedOrders.has(d))) return t;
      const dependencies = t.dependencies.filter(d => !removedOrders.has(d));
      const dependency_classes = { ...t.dependency_classes };
      removedOrders.forEach(o => delete dependency_classes[o]);
      return { ...t, dependencies, dependency_classes };
    });
}

/** True iff at least one task in the template depends on another. A template where NOT EVEN ONE
 *  task has a dependency is almost certainly missing its sequencing — flagged as a save/publish
 *  warning (non-blocking) rather than silently accepted. */
export function hasAnyDependency(tasks: Task[]): boolean {
  return tasks.some(t => t.dependencies.length > 0);
}

/** Direct children of a task, keyed by WBS (deriveParentUid). */
export function directChildren(tasks: Task[], uid: string): Task[] {
  return tasks.filter(t => deriveParentUid(t.task_uid) === uid);
}

/**
 * Round a days/hours estimate to 2 decimal places. Repeatedly summing many leaves' floats (see
 * computeEstimate) accumulates binary floating-point drift (e.g. 0.1 + 0.2 → 0.30000000000000004);
 * rounding at every return point — not just the outermost one — keeps that drift from compounding
 * through the recursion and out into both the Inspector's display and the persisted JSON.
 */
export function roundEstimate(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * A parent task's Est. days/hours are DERIVED, never user-entered: the sum of its children's
 * estimates, recursively — so a grandparent's total naturally rolls up every descendant leaf.
 * A leaf (no children) reports its own stored value. Used both to render the read-only Timing
 * fields for a parent in the Inspector and to compute the value actually persisted on save
 * (see serializeTasksForStorage), so a parent's stored field is never trusted once it has children.
 */
export function computeEstimate(tasks: Task[], uid: string): { days: number; hours: number } {
  const children = directChildren(tasks, uid);
  if (!children.length) {
    const t = tasks.find(x => x.task_uid === uid);
    return { days: roundEstimate(t?.estimatedDays ?? 0), hours: roundEstimate(t?.estimatedHours ?? 0) };
  }
  const sum = children.reduce((acc, c) => {
    const sub = computeEstimate(tasks, c.task_uid);
    return { days: acc.days + sub.days, hours: acc.hours + sub.hours };
  }, { days: 0, hours: 0 });
  return { days: roundEstimate(sum.days), hours: roundEstimate(sum.hours) };
}

// ── Dependency graph / cycle detection (edges keyed by legacy task_order) ──
type DepGraph = Record<number, number[]>;
export function buildDepGraph(tasks: Task[]): DepGraph {
  const g: DepGraph = {};
  tasks.forEach(t => { g[t.task_order] = [...(t.dependencies || [])]; });
  return g;
}
export function canReach(graph: DepGraph, from: number, to: number, seen: Set<number> = new Set()): boolean {
  if (from === to) return true;
  if (seen.has(from)) return false;
  seen.add(from);
  return (graph[from] || []).some(next => canReach(graph, next, to, seen));
}
/** Would "taskOrder depends on depOrder" (already added) create a cycle? Check AFTER adding, and flag the row red. */
export function isDependencyCyclic(tasks: Task[], taskOrder: number, depOrder: number): boolean {
  const graph = buildDepGraph(tasks);
  return canReach(graph, depOrder, taskOrder);
}
export function findCyclePath(tasks: Task[], taskOrder: number, depOrder: number): string[] {
  const graph = buildDepGraph(tasks);
  const byOrder = new Map(tasks.map(t => [t.task_order, t]));
  const path: number[] = [depOrder];
  function dfs(cur: number, target: number, seen: Set<number>): boolean {
    if (cur === target) return true;
    seen.add(cur);
    for (const next of graph[cur] || []) {
      if (seen.has(next)) continue;
      path.push(next);
      if (dfs(next, target, seen)) return true;
      path.pop();
    }
    return false;
  }
  dfs(depOrder, taskOrder, new Set());
  path.push(taskOrder);
  return path.map(o => byOrder.get(o)?.taskName ?? String(o));
}

// ── Version / scope resolution ───────────────────────────────────────
/** status stays 'active' on every row; invalid:false marks the ONE current version. */
export function activeVersion(versions: TemplateVersion[]): TemplateVersion | undefined {
  return versions.find(v => v.invalid === false) ?? versions[versions.length - 1];
}

export function hasTradeTemplate(trade: Trade): boolean {
  return !!(trade.provider && trade.provider.versions.length);
}

export function scopeVersionsFor(trade: Trade | null, globalDefault: GlobalDefaultTemplate, scope: Scope, tenantId: string | null): TemplateVersion[] {
  if (scope === 'global') return globalDefault.versions;
  if (!trade) return [];
  if (scope === 'provider') return trade.provider ? trade.provider.versions : [];
  return tenantId ? trade.tenants[tenantId]?.versions ?? [] : [];
}

export function scopeLabel(scope: Scope, tenantId: string | null, tenants: Tenant[]): string {
  if (scope === 'global') return 'Global Default Template';
  if (scope === 'provider') return 'Provider default';
  return tenants.find(t => t.id === tenantId)?.name ?? 'Tenant';
}

/** Human-readable source of a fork, resolving a source tenant id to its name (for lineage display). */
export function forkedFromLabel(from: ForkPointer, tenants: Tenant[]): string {
  const ver = from.version ? ` ${from.version}` : '';
  if (from.scope === 'provider') return `Provider default${ver}`;
  if (from.scope === 'global') return `Global Default${ver}`;
  const name = tenants.find(t => t.id === from.tenantId)?.name ?? from.tenantId ?? 'a tenant';
  return `Tenant ${name}${ver}`;
}

/** Trades a tenant has adopted (has a scope for) — drives the Library "By tenant" grouping. */
export function tradesForTenant(trades: Trade[], tenantId: string): Trade[] {
  return trades.filter(t => t.tenants[tenantId]);
}

/** Append a new version to a scope's history, archiving (invalid:true) all prior versions. */
export function appendVersion(existing: TemplateVersion[], tasks: Task[], note: string, forkedFrom: TemplateVersion['forkedFrom'] = null): { next: TemplateVersion; all: TemplateVersion[] } {
  const archived = existing.map(v => ({ ...v, invalid: true }));
  const nums = archived.map(v => parseInt(v.version.slice(1), 10)).filter(n => !isNaN(n));
  const version = `v${(nums.length ? Math.max(...nums) : 0) + 1}`;
  const next: TemplateVersion = { version, note, when: new Date().toISOString(), tasks, forkedFrom, status: 'active', invalid: false };
  return { next, all: [...archived, next] };
}
